import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { ArrowLeft, Copy, RefreshCw } from "lucide-react";
import StatusBadge from "./StatusBadge";
import TemplatePreview from "./TemplatePreview";
import {
  getTemplate,
  isAgentNotConfigured,
  refreshTemplateStatus,
} from "./rcsTemplateApi";
import { displayFormattedDate, typeLabel } from "./templatePayload";

const POLL_INTERVAL_MS = 60 * 1000;

const STATUS_TEXT = {
  pending_review: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
};

const Row = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-b border-gray-100 last:border-0">
    <div className="sm:w-40 text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-sm text-gray-700 min-w-0 break-all">{children}</div>
  </div>
);

const TemplateDetail = ({ nav, onAgentMissing, id }) => {
  const [template, setTemplate] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const handleFailure = useCallback(
    (res) => {
      if (isAgentNotConfigured(res)) {
        onAgentMissing();
      } else if (res.status === 404) {
        toast.error("Template not found");
        nav.list();
      } else {
        toast.error(res.error);
      }
    },
    // nav is recreated each render of the shell; its behaviour is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onAgentMissing]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getTemplate(id);
      if (cancelled) return;
      if (res.ok) setTemplate(res.data.data);
      else handleFailure(res);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, handleFailure]);

  const status = template?.status;

  // Poll while pending; the backend also re-checks JioCX every 5 minutes.
  useEffect(() => {
    if (status !== "pending_review") return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      const res = await getTemplate(id);
      if (cancelled || !res.ok) return;
      setTemplate(res.data.data);
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id, status]);

  const refresh = async () => {
    setRefreshing(true);
    const res = await refreshTemplateStatus(id);
    setRefreshing(false);
    if (res.ok) {
      const updated = res.data.data;
      if (updated.status !== template.status) {
        toast.info(`Status changed to ${STATUS_TEXT[updated.status] || updated.status}`);
      } else {
        toast.info("Status is still " + (STATUS_TEXT[updated.status] || updated.status));
      }
      setTemplate(updated);
    } else if (res.status === 404 || isAgentNotConfigured(res)) {
      handleFailure(res);
    } else {
      Swal.fire({ title: "Couldn't refresh status", text: res.error, icon: "error" });
    }
  };

  const copyVendorId = async () => {
    try {
      await navigator.clipboard.writeText(template.vendor_template_id);
      toast.success("Copied");
    } catch (e) {
      toast.error("Couldn't copy");
    }
  };

  const back = (
    <button
      onClick={() => nav.list()}
      className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
      title="Back to templates"
    >
      <ArrowLeft size={18} />
    </button>
  );

  if (!template) {
    return (
      <div>
        <div className="mb-6">{back}</div>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00b6ce]" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {back}
        <h1 className="text-2xl font-bold text-gray-800 break-all">{template.template_name}</h1>
        <StatusBadge status={template.status} />
        <div className="flex flex-wrap gap-2 ml-auto">
          {status === "pending_review" && (
            <button
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#00b6ce] text-[#00b6ce] text-sm font-semibold hover:bg-[#00b6ce]/5 disabled:opacity-50"
            >
              <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Checking…" : "Refresh status"}
            </button>
          )}
          {status === "approved" && (
            <button
              onClick={() => nav.send(template.id)}
              className="px-4 py-2 rounded-lg bg-[#00b6ce] hover:bg-[#009baf] text-white text-sm font-semibold"
            >
              Send
            </button>
          )}
          {status === "rejected" && (
            <button
              onClick={() => nav.create(template.id)}
              className="px-4 py-2 rounded-lg bg-[#00b6ce] hover:bg-[#009baf] text-white text-sm font-semibold"
            >
              Create new version
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
        <div className="space-y-4">
          {status === "rejected" && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="text-sm font-semibold text-red-700 mb-1">Rejected</div>
              <div className="text-sm text-red-700 whitespace-pre-wrap">
                {template.rejection_reason || "No reason was given."}
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-3">
            <Row label="Type">{typeLabel(template.template_type)}</Row>
            <Row label="Status">
              <StatusBadge status={template.status} />
            </Row>
            <Row label="Template ID">
              {template.vendor_template_id ? (
                <span className="inline-flex items-center gap-2">
                  <code className="text-xs bg-gray-50 px-1.5 py-0.5 rounded">
                    {template.vendor_template_id}
                  </code>
                  <button
                    onClick={copyVendorId}
                    className="text-gray-400 hover:text-[#00b6ce]"
                    title="Copy"
                  >
                    <Copy size={14} />
                  </button>
                </span>
              ) : (
                "—"
              )}
            </Row>
            <Row label="Created">{displayFormattedDate(template.inserted_at)}</Row>
            <Row label="Last checked">
              {displayFormattedDate(template.last_status_checked_at) || "—"}
            </Row>
            {template.created_by && <Row label="Created by">{template.created_by}</Row>}
            {template.variable_fields?.length > 0 && (
              <Row label="Variables">
                {template.variable_fields.map((f) => `${f.path} (${f.count})`).join(", ")}
              </Row>
            )}
          </div>
          {status === "pending_review" && (
            <p className="text-xs text-gray-400">
              This template is under review. This page checks for updates every minute while
              it's open.
            </p>
          )}
        </div>
        <div>
          <div className="text-sm font-semibold text-gray-600 mb-2 text-center">Preview</div>
          <TemplatePreview data={template.data} />
        </div>
      </div>
    </div>
  );
};

export default TemplateDetail;
